import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect } from 'react-redux'
import i18n from "i18next";
import { Modal, Checkbox, Button, Progress, Space, Typography, Alert, Collapse, Radio } from "antd";
import JSZip, { forEach } from "jszip";
import { SubjectRepository, SkosRepository, SkosScheme, RdfTriplet } from '../../redux';
import { lastElementCardURI } from '../../utils/uriUtil';

const { Text, Link, Title } = Typography;

const OPT = {
  CARD: "card_asset",
  CLASS: "classification",
  RELATED: "relatedRefs"
};

const LABEL_KEYS = {
  [OPT.CLASS]: "io.export.conceptscheme.includeConcepts",
  [OPT.RELATED]: "io.export.conceptscheme.includeRelated"
};

const defaultChecked = [OPT.CLASS, OPT.RELATED];

const FORMAT = {
  N_TRIPLES: "N-Triples",
  TURTLE: "Turtle",
};

function ExportConceptScheme({
  open,
  onClose,
  dispatch,
  singleIRI = false,
  IRI = null,
  conditions = [],
  graph = "http://localhost/sksa/graph"
}) {
  const [checked, setChecked] = useState(defaultChecked);
  const [format, setFormat] = useState(FORMAT.N_TRIPLES);

  const [isExporting, setIsExporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusText, setStatusText] = useState("");
  const [error, setError] = useState(null);

  const [zipBlob, setZipBlob] = useState(null);
  const [zipUrl, setZipUrl] = useState("");

  // --- NEW: Log ---
  const [logLines, setLogLines] = useState([]);
  const [logCollapsed, setLogCollapsed] = useState(true); // управляем авто-сворачиванием


  
  const downloadTriggeredRef = useRef(false);

  const isExportDone = !!zipUrl;

  const zipFileName = useMemo(() => {
    const fileName = IRI ? lastElementCardURI(IRI) : "all";
    const stamp = new Date().toISOString().replaceAll(":", "-").replaceAll(".", "-");
    return `export_scheme_${fileName}_${stamp}.zip`;
  }, [IRI]);

  // --- Cleanup object URL ---
  useEffect(() => {
    return () => {
      if (zipUrl) URL.revokeObjectURL(zipUrl);
    };
  }, [zipUrl]);

  // При открытии: дефолты, убрать недоступные пункты
  useEffect(() => {
    if (!open) return;

    setChecked(() => {
      const next = new Set(defaultChecked);
      return Array.from(next);
    });
    setFormat(FORMAT.N_TRIPLES);


    resetRuntimeState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function resetRuntimeState() {
    setIsExporting(false);
    setProgress(0);
    setStatusText("");
    setError(null);
    downloadTriggeredRef.current = false;

    // NEW: reset log
    setLogLines([]);
    setLogCollapsed(true);

    if (zipUrl) {
      URL.revokeObjectURL(zipUrl);
      setZipUrl("");
    }
  }

  function pushLog(line) {
    const ts = new Date().toISOString().slice(11, 19);
    setLogLines((prev) => [...prev, `[${ts}] ${line}`]);
  }

  // --- tasks for export ---
  function buildTasks(IRIs) {
    const selected = new Set(checked);
    const list = [];

    const exportIRIs = uniqueIRIs(Array.isArray(IRIs) && IRIs.length ? IRIs : (IRI ? [IRI] : []));

    const taskIRIs = uniqueIRIs(exportIRIs);
    for (let index = 0; index < taskIRIs.length; index++) {
      const IRI = exportIRIs[index];
      list.push({
        key: OPT.CARD,
        includeConcepts: selected.has(OPT.CLASS),
        includeRelated: selected.has(OPT.RELATED),
        info: {"scheme": IRI, "IRIs": [IRI]},
        id: ""+(index+1)+"_"+lastElementCardURI(IRI),
        title: i18n.t("io.export.conceptscheme.fetchingData", { name: lastElementCardURI(IRI) }),
        run: () => SubjectRepository.exportSubject(graph, [IRI], getOutputFormat(format)),
      });
    }
    
    return list;
  }
  
  
  async function handleExport() {
    setError(null);
    setIsExporting(true);
    setProgress(0);
    setStatusText(i18n.t("io.export.conceptscheme.preparing"));
    setZipBlob(null);

    // лог разворачиваем
    setLogCollapsed(false);
    pushLog(i18n.t("io.export.conceptscheme.log.started"));

    if (zipUrl) {
      URL.revokeObjectURL(zipUrl);
      setZipUrl("");
    }

    try {
      const lines = [];
      const info = [];
      const zip = new JSZip();

      let exportIRIs = [IRI];
      if (!singleIRI) {
        setStatusText(i18n.t("io.export.conceptscheme.fetchingIriList"));
        pushLog("SkosRepository.loadIRIVocabs");
        exportIRIs = await dispatch(SkosRepository.loadIRISchemes(conditions));
      }
      const exportTasks = buildTasks(exportIRIs);
      
      const headerLines = [
        `Export for ConceptScheme: ${IRI}`,
        `Generated at: ${new Date().toISOString()}`,
        `Selected sections: ${checked.length ? checked.join(", ") : "(none)"}`,
        "",
      ];
      lines.push(...headerLines);

      const total = exportTasks.length;
      for (let i = 0; i < exportTasks.length; i += 1) {
        const t = exportTasks[i];
        setStatusText(i18n.t("io.export.conceptscheme.exporting", { title: t.title }));
        pushLog(i18n.t("io.export.conceptscheme.log.exporting", { title: t.title }));

        if (t.includeRelated || t.includeConcepts){
          pushLog(i18n.t("io.export.conceptscheme.log.searchingRelated", { id: t.id }));
          const related = t.includeRelated
            ? await RdfTriplet.loadSubject(t.info.scheme, graph) 
            : null;
          const relatedIRIs = t.includeRelated
            ? related.bindings
                .filter(item => item.predicat.value !== "http://www.w3.org/1999/02/22-rdf-syntax-ns#type")
                .filter(item => (item.object.type === "uri") && (item.predicat.value !== "http://www.w3.org/2004/02/skos/core#topConceptOf"))
                .map(item => item.object.value)
            : [];
          const concepts = (t.includeConcepts || t.includeRelated)
            ? await RdfTriplet.loadObject(t.info.scheme, graph)
            : null;
          const schemeConceptIRIs = concepts
            ? concepts.bindings
                .filter(item => item.predicat.value !== "http://www.w3.org/1999/02/22-rdf-syntax-ns#type")
                .filter(item => item.predicat.value === "http://www.w3.org/2004/02/skos/core#inScheme")
                .map(item => item.subject.value)
            : [];
          const conceptIRIs = t.includeConcepts ? schemeConceptIRIs : [];
          const collections = t.includeRelated && schemeConceptIRIs.length
            ? await dispatch(SkosScheme.getCollectionByConcepts(schemeConceptIRIs, graph))
            : null;
          const collectionIRIs = collections?.response?.results?.bindings
            ?.map(item => item.collection?.value)
            .filter(Boolean) ?? [];
          const IRIs = uniqueIRIs([t.info.scheme, ...relatedIRIs, ...conceptIRIs, ...collectionIRIs]);
          if (IRIs.length>1){
            t.info.IRIs = IRIs;
            t.run = () => SubjectRepository.exportSubject(graph, IRIs, getOutputFormat(format))
          };
        };
        const exportIRIs = await includeBlankNodeIRIs(t.info.IRIs, graph, pushLog);
        t.info.IRIs = exportIRIs;
        const result = await SubjectRepository.exportSubject(
          graph,
          exportIRIs,
          getOutputFormat(format)
        );

        lines.push(`=== ${t.title} ===`);

        if (t.key === OPT.CARD) {
          const file = `${t.id}.${format === FORMAT.TURTLE ? "ttl" : "n3"}`;
          info.push({"card":t.info, "file":file, "format":format});
          zip.file(file, `${result}\n`);
          pushLog(i18n.t("io.export.conceptscheme.log.fileAdded", { file }));
          lines.push(`==> ${i18n.t("io.export.conceptscheme.log.fileAdded", { file })}`);
        }

        lines.push("");

        const pct = Math.round(((i + 1) / total) * 90);
        setProgress(pct);
      }

      setStatusText(i18n.t("io.export.conceptscheme.packingZip"));
      pushLog(i18n.t("io.export.conceptscheme.packingZip"));

      zip.file("export.log", lines.join("\n"));
      zip.file("export.json", JSON.stringify(info));

      const blob = await zip.generateAsync({ type: "blob" }, (meta) => {
        const pct = 90 + Math.round((meta.percent / 100) * 10);
        setProgress(Math.min(100, Math.max(90, pct)));
      });

      setZipBlob(blob);

      const url = URL.createObjectURL(blob);
      setZipUrl(url);
      setStatusText(i18n.t("io.export.conceptscheme.zipReady"));
      setProgress(100);

      pushLog(i18n.t("io.export.conceptscheme.log.zipCreated", { filename: zipFileName }));
      triggerDownload(url, zipFileName);
      downloadTriggeredRef.current = true;

      setIsExporting(false);
      setStatusText(i18n.t("io.export.conceptscheme.done"));
      pushLog(i18n.t("io.export.conceptscheme.log.completed"));

      // После окончания экспорта лог свёртывается
      setLogCollapsed(true);

    } catch (e) {
      setIsExporting(false);
      setProgress(0);
      setStatusText("");
      setError(e?.message || String(e));
      pushLog(i18n.t("io.export.conceptscheme.log.error", { error: e?.message || String(e) }));
      // при ошибке можно оставить лог раскрытым
      setLogCollapsed(false);
    }
  }

  function handleClose() {
    resetRuntimeState();
    onClose?.();
  }

  const primaryButtonLabel = i18n.t(isExportDone
    ? "io.export.conceptscheme.close"
    : "io.export.conceptscheme.export");
  const primaryOnClick = isExportDone ? handleClose : handleExport;

  // Показывать блок прогресса, если идёт экспорт или идёт проверка графов или экспорт завершён
  const showProgressBlock = isExporting || isExportDone;

  const logItems = [
    {
      key: "log",
      label: i18n.t("io.export.conceptscheme.executionLog"),
      children: (
        <div
          style={{
            maxHeight: 220,
            overflow: "auto",
            whiteSpace: "pre-wrap",
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
            fontSize: 12,
            lineHeight: 1.35,
            background: "#fafafa",
            border: "1px solid #f0f0f0",
            borderRadius: 8,
            padding: 10,
          }}
        >
          {logLines.length ? logLines.join("\n") : i18n.t("io.export.conceptscheme.emptyLog")}
        </div>
      ),
    },
  ];

  return (
    <Modal
      title={<Title level={5} style={{ margin: 0 }}>{i18n.t("io.export.conceptscheme.title")}</Title>}
      open={open}
      onCancel={handleClose}
      maskClosable={!isExporting }
      closable={!isExporting }
      destroyOnHidden={false}
      footer={
        <Space>
          {!isExportDone ? (
            <Button onClick={handleClose} disabled={isExporting}>
              {i18n.t("io.export.conceptscheme.cancel")}
            </Button>
          ) : null}
          <Button
            type="primary"
            onClick={primaryOnClick}
            loading={isExporting}
            disabled={isExporting || (!zipUrl && (IRI === undefined && Array.isArray(conditions)))}
          >
            {primaryButtonLabel}
          </Button>
        </Space>
      }
    >
      <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
        <Text type="secondary">
          {i18n.t("io.export.conceptscheme.description")}
        </Text>

        <Space orientation="vertical" size={4}>
          <Text>{i18n.t("io.export.conceptscheme.format")}</Text>
          <Radio.Group
            value={format}
            onChange={(e) => setFormat(e.target.value)}
            disabled={isExporting || isExportDone}
          >
            <Radio value={FORMAT.TURTLE}>{i18n.t("io.export.conceptscheme.formats.turtle")}</Radio>
            <Radio value={FORMAT.N_TRIPLES}>{i18n.t("io.export.conceptscheme.formats.nTriples")}</Radio>
          </Radio.Group>
        </Space>

        <Space orientation="vertical" size={8} style={{ width: "100%" }}>

          <Checkbox
            checked={checked.includes(OPT.CLASS)}
            onChange={(e) => toggleChecked(OPT.CLASS, e.target.checked, checked, setChecked)}
            disabled={isExporting || isExportDone }
          >
            {i18n.t(LABEL_KEYS[OPT.CLASS])}
          </Checkbox>

          <Checkbox
            checked={checked.includes(OPT.RELATED)}
            onChange={(e) => toggleChecked(OPT.RELATED, e.target.checked, checked, setChecked)}
            disabled={isExporting || isExportDone }
          >
            {i18n.t(LABEL_KEYS[OPT.RELATED])}
          </Checkbox>

        </Space>

        {showProgressBlock ? (
          <Space orientation="vertical" style={{ width: "100%" }}>
            {/* Экспортный прогресс */}
            {(isExporting || isExportDone) ? (
              <>
                <Progress percent={progress} />
                {statusText ? <Text>{statusText}</Text> : null}
              </>
            ) : null}

            {/* Лог под прогрессбаром */}
            <Collapse
              items={logItems}
              activeKey={logCollapsed ? [] : ["log"]}
              onChange={(keys) => setLogCollapsed(!(Array.isArray(keys) ? keys.includes("log") : false))}
            />
          </Space>
        ) : null}

        {error ? (
          <Alert
            type="error"
            showIcon
            message={i18n.t("io.export.conceptscheme.exportError")}
            description={<Text>{error}</Text>}
          />
        ) : null}

        {isExportDone ? (
          <Space orientation="vertical" style={{ width: "100%" }}>
            <Text strong>{i18n.t("io.export.conceptscheme.downloadLink")}</Text>
            <Link href={zipUrl} download={zipFileName}>
              {i18n.t("io.export.conceptscheme.download", { filename: zipFileName })}
            </Link>
            <Text type="secondary">
              {i18n.t("io.export.conceptscheme.downloadHint")}
            </Text>
          </Space>
        ) : null}
      </Space>
    </Modal>
  );
}

// --- helpers ---

function toggleChecked(key, next, checked, setChecked) {
  const set = new Set(checked);
  if (next) set.add(key);
  else set.delete(key);
  setChecked(Array.from(set));
}

function isPlainEmptyObject(v) {
  if (!v || typeof v !== "object") return true;
  if (Array.isArray(v)) return v.length === 0;
  return Object.keys(v).length === 0;
}

function uniqueIRIs(IRIs) {
  return [...new Set((Array.isArray(IRIs) ? IRIs : []).filter(Boolean))];
}

function getOutputFormat(format) {
  return format === FORMAT.TURTLE ? "TTL" : "N-TRIPLE";
}

async function includeBlankNodeIRIs(initialIRIs, graph, onIteration) {
  const allIRIs = uniqueIRIs(initialIRIs);
  const knownIRIs = new Set(allIRIs);
  let subjects = [...allIRIs];

  for (let iteration = 1; iteration <= 10 && subjects.length; iteration += 1) {
    const blankNodeIRIs = [];

    // Запросы выполняются последовательно: RdfTriplet.loadSubject использует
    // общий экземпляр запроса, поэтому параллельные вызовы могут подменить subject.
    for (const subject of subjects) {
      const triples = await RdfTriplet.loadSubject(subject, graph);
      for (const item of triples?.bindings ?? []) {
        const isBlankNode = item.object?.type === "bnode"
          || item.object?.value?.startsWith("nodeID://");
        if (!isBlankNode) continue;
        const blankNodeIRI = item.object.value;
        if (!blankNodeIRI || knownIRIs.has(blankNodeIRI)) continue;
        knownIRIs.add(blankNodeIRI);
        allIRIs.push(blankNodeIRI);
        blankNodeIRIs.push(blankNodeIRI);
      }
    }

    if (!blankNodeIRIs.length) break;
    onIteration?.(`Получение анонимных узлов: итерация ${iteration}, найдено ${blankNodeIRIs.length}`);
    subjects = blankNodeIRIs;
  }

  return allIRIs;
}

function triggerDownload(url, filename) {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

// Redux mapping (как у тебя)
const mapStateToProps = (state, ownProps) => {
  const { scheme, vset } = state;
  const singleIRI = ownProps.IRI !== undefined && ownProps.IRI !== null && ownProps.IRI !== "";
  return {
    ...ownProps,
    singleIRI,
    IRI: singleIRI ? ownProps.IRI : null,
    conditions: ownProps.conditions ?? [],
    graph: vset.graph.signed
  };
};

export default connect(mapStateToProps)(ExportConceptScheme);
